/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: listingTypes and listingFields are managed locally in this file. The
// fallback in `mergeListingConfig` (src/util/configHelpers.js) reads from
// here when the hosted listing-types / listing-fields assets are empty.
// Pushing content to those hosted assets still wins, so an admin could swap
// configurations from Console without a code deploy if ever needed.

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be 'public', 'private', or 'metadata'.
 *                                  Default value: 'public'.
 * - schemaType (optional):         'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options for 'enum' / 'multi-enum'.
 * - listingTypeConfig (optional):  { limitToListingTypeIds, listingTypeIds }
 * - categoryConfig (optional):     { limitToCategoryIds, categoryIds }
 *                                  When `categoryIds` includes a parent id (e.g. `'klar'`), the
 *                                  field is automatically applied to every descendant category too.
 * - filterConfig:                  { indexForSearch, showFilter, filterType, label, group, searchMode }
 *                                  Set `indexForSearch: true` only after running
 *                                  `flex-cli search-schema set` for the matching public data key.
 * - showConfig:                    { label, isDetail }
 * - saveConfig:                    { label, placeholderMessage, isRequired, requiredMessage }
 *                                  Omit `saveConfig` to hide the field from the EditListing form
 *                                  entirely (used for AI-driven hidden fields like `plaggtype`,
 *                                  `premium`, `sesong`).
 *
 * AI integration note: fields marked "AI auto + bekreft" or "AI auto skjult" in the spec are
 * regular fields here. The AI wiring (vision + classifier filling these on submit) lives in a
 * separate workstream — for now sellers see the visible AI-fields as optional inputs they can
 * fill manually, and the hidden ones (no `saveConfig`) just stay empty until AI is wired.
 *
 * An enum option can carry `categoryIds` (offered only in those categories, see
 * pickEnumOptionsForCategories in util/fieldHelpers.js) and `group` (listed as a group of its
 * own after the options before it).
 */

// The colours are shared with the server (util/listingSearchText.js).
import COLOUR_OPTIONS from './colourOptions';

// Waist sizes in inches, W24 to W34, for trousers (Guro, 26 October: KBM lists jeans by
// waist). Their own keys, so W34 never clashes with EU size 34. Offered only in these
// categories, after the other sizes. Every view shows a size by upper-casing its key
// (w28 → W28).
const WAIST_SIZE_CATEGORY_IDS = ['klar-bukser', 'klar-jeans', 'klar-shorts'];
const WAIST_SIZE_OPTIONS = [24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34].map(inches => ({
  option: `w${inches}`,
  label: `W${inches}`,
  group: 'waist',
  categoryIds: WAIST_SIZE_CATEGORY_IDS,
}));

export const listingFields = [
  // Shipping product (Posten Liten vs Stor). Selected on the Frakt
  // tab; persisted on `publicData.shippingProduct` and drives both
  // buyer-side checkout (price + optional pickup-point picker) and
  // seller-side post-purchase view (sendekode vs QR + label). Source
  // of truth for label + price + dims is
  // `src/config/shippingProducts.js`. No `saveConfig` here because
  // the field doesn't render in the generic listing-fields grid —
  // it's owned entirely by EditListingDeliveryPanel.
  {
    key: 'shippingProduct',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'posten-liten', label: 'Posten Norgespakke Liten' },
      { option: 'posten-stor', label: 'Posten Norgespakke Stor' },
    ],
    showConfig: { label: 'Frakt', isDetail: false },
  },
  // ---------- Required ----------
  {
    key: 'tilstand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ny-med-merkelapp', label: 'Ny med merkelapp' },
      { option: 'som-ny', label: 'Som ny' },
      { option: 'pent-brukt', label: 'Pent brukt' },
      { option: 'god-stand', label: 'God stand' },
    ],
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tilstand',
      group: 'primary',
    },
    showConfig: { label: 'Tilstand', isDetail: true },
    saveConfig: {
      label: 'Tilstand',
      placeholderMessage: 'Velg tilstand…',
      isRequired: true,
      requiredMessage: 'Du må velge tilstand.',
    },
  },
  {
    key: 'storrelse-klar',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'xxs', label: 'XXS' },
      { option: 'xs', label: 'XS' },
      { option: 's', label: 'S' },
      { option: 'm', label: 'M' },
      { option: 'l', label: 'L' },
      { option: 'xl', label: 'XL' },
      { option: 'xxl', label: 'XXL' },
      { option: '32', label: '32' },
      { option: '34', label: '34' },
      { option: '36', label: '36' },
      { option: '38', label: '38' },
      { option: '40', label: '40' },
      { option: '42', label: '42' },
      { option: '44', label: '44' },
      { option: '46', label: '46' },
      { option: '48', label: '48' },
      { option: '50', label: '50' },
      { option: '52', label: '52' },
      ...WAIST_SIZE_OPTIONS,
    ],
    categoryConfig: { limitToCategoryIds: true, categoryIds: ['klar'] },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Størrelse',
      group: 'primary',
    },
    showConfig: { label: 'Størrelse', isDetail: true },
    saveConfig: {
      label: 'Størrelse',
      placeholderMessage: 'Velg størrelse…',
      isRequired: true,
      requiredMessage: 'Du må velge en størrelse.',
    },
  },
  {
    key: 'storrelse-sko',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '35', label: '35' },
      { option: '36', label: '36' },
      { option: '37', label: '37' },
      { option: '38', label: '38' },
      { option: '39', label: '39' },
      { option: '40', label: '40' },
      { option: '41', label: '41' },
      { option: '42', label: '42' },
      { option: '43', label: '43' },
      { option: '44', label: '44' },
      { option: '45', label: '45' },
      { option: '46', label: '46' },
    ],
    categoryConfig: { limitToCategoryIds: true, categoryIds: ['sko'] },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Skostørrelse',
      group: 'primary',
    },
    showConfig: { label: 'Skostørrelse', isDetail: true },
    saveConfig: {
      label: 'Skostørrelse',
      placeholderMessage: 'Velg størrelse…',
      isRequired: true,
      requiredMessage: 'Du må velge en størrelse.',
    },
  },
  // ---------- AI auto + bekreft (optional in form for now; AI fills later) ----------
  // `merke` is ALSO managed as a hosted listing field in Sharetribe
  // Console; the hosted version overrides this local config at
  // runtime via orderPreservingUnion in configHelpers.js. This local
  // entry exists for two reasons:
  //
  //   1. Position anchor — the filter strip / details list layout is
  //      driven by the local file's array order, so removing this
  //      block would push `merke` to the end of the list (hosted-only
  //      entries get appended after locals).
  //   2. SSR / cold-start fallback — if the hosted-assets fetch fails
  //      or is still pending on first paint, the local options
  //      render so the field isn't blank.
  //
  // Keep the option keys here in sync with Console; the labels and
  // search settings can drift (Console wins). Add new brands in
  // Console first, then mirror the option key here when convenient.
  {
    key: 'merke',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'other-stories', label: '&Other Stories' },
      { option: 'acne', label: 'Acne' },
      { option: 'arket', label: 'Arket' },
      { option: 'brgn', label: 'BRGN' },
      { option: 'busnel', label: 'Busnel' },
      { option: 'by-malene-birger', label: 'By Malene Birger' },
      { option: 'bytimo', label: 'ByTimo' },
      { option: 'cos', label: 'COS' },
      { option: 'cubus', label: 'Cubus' },
      { option: 'filippa-k', label: 'Filippa K' },
      { option: 'fransa', label: 'Fransa' },
      { option: 'ganni', label: 'Ganni' },
      { option: 'gant', label: 'Gant' },
      { option: 'hm', label: 'H&M' },
      { option: 'holebrook', label: 'Holebrook' },
      { option: 'holzweiler', label: 'Holzweiler' },
      { option: 'inwear', label: 'In Wear' },
      { option: 'kappahl', label: 'Kappahl' },
      { option: 'lindex', label: 'Lindex' },
      { option: 'mango', label: 'Mango' },
      { option: 'part-two', label: 'Part Two' },
      { option: 'rains', label: 'Rains' },
      { option: 'samsoe-samsoe', label: 'Samsøe Samsøe' },
      { option: 'stine-goya', label: 'Stine Goya' },
      { option: 'tiger-of-sweden', label: 'Tiger of Sweden' },
      { option: 'vero-moda', label: 'Vero Moda' },
      { option: 'zara', label: 'Zara' },
      { option: 'annet', label: 'Annet' },
    ],
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Merke',
      group: 'primary',
    },
    showConfig: { label: 'Merke', isDetail: true },
    saveConfig: {
      label: 'Merke',
      placeholderMessage: 'Velg merke…',
      isRequired: false,
    },
  },
  // Free-text brand, only offered when `merke` is "annet". Without it the
  // actual brand was lost every time a seller picked Annet — the seller
  // knows it, the buyer wants it, and the dropdown can never list every
  // brand. It is display data (ListingCard / ProductHeader show it in place
  // of "Annet"); the enum filter stays the search surface.
  //
  // No `filterConfig` at all, on purpose: the validator in configHelpers
  // requires a label inside it, and a field that fails validation is dropped
  // from the merged config without an error — which is exactly how this
  // field went missing on staging the first time.
  {
    key: 'merke-annet',
    scope: 'public',
    schemaType: 'text',
    showConfig: { label: 'Merke', isDetail: false },
    saveConfig: {
      label: 'Hvilket merke?',
      placeholderMessage: 'Skriv inn merket',
      isRequired: false,
    },
  },
  {
    key: 'materiale',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'akryl', label: 'Akryl' },
      { option: 'alpakka', label: 'Alpakka' },
      { option: 'angora', label: 'Angora' },
      { option: 'bomull', label: 'Bomull' },
      { option: 'denim', label: 'Denim' },
      { option: 'elastan', label: 'Elastan' },
      { option: 'kashmir', label: 'Kashmir' },
      { option: 'lin', label: 'Lin' },
      { option: 'lyocell', label: 'Lyocell' },
      { option: 'mohair', label: 'Mohair' },
      { option: 'nylon', label: 'Nylon' },
      { option: 'polyester', label: 'Polyester' },
      { option: 'silke', label: 'Silke' },
      { option: 'skinn', label: 'Skinn' },
      { option: 'tencel', label: 'Tencel' },
      { option: 'ull', label: 'Ull' },
      { option: 'viskose', label: 'Viskose' },
      { option: 'annet', label: 'Annet' },
    ],
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Materiale',
      group: 'secondary',
      searchMode: 'has_any',
    },
    showConfig: { label: 'Materiale', isDetail: true },
    saveConfig: {
      label: 'Materiale (maks 3)',
      placeholderMessage: 'Velg materialer…',
      isRequired: false,
    },
  },
  {
    key: 'farge',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: COLOUR_OPTIONS,
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Farge',
      group: 'primary',
      searchMode: 'has_any',
    },
    showConfig: { label: 'Farge', isDetail: true },
    saveConfig: {
      label: 'Farge',
      placeholderMessage: 'Velg farger…',
      isRequired: false,
    },
  },
  {
    key: 'passform',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'figurnaer', label: 'Figurnær' },
      { option: 'normal', label: 'Normal' },
      { option: 'oversized', label: 'Oversized' },
    ],
    categoryConfig: { limitToCategoryIds: true, categoryIds: ['klar'] },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Passform',
      group: 'secondary',
    },
    showConfig: { label: 'Passform', isDetail: true },
    saveConfig: {
      label: 'Passform',
      placeholderMessage: 'Velg passform…',
      isRequired: false,
    },
  },

  // ---------- AI auto skjult (no saveConfig — never shown in form) ----------
  // These get populated by the AI tagging job after publish. Until that's
  // wired, the fields exist on listings but stay empty.
  {
    key: 'plaggtype',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      // Strikk-overlapp
      { option: 'tag-strikk', label: 'Strikk' },
      // Materiale-cross
      { option: 'tag-denim', label: 'Denim' },
      { option: 'tag-fleece', label: 'Fleece' },
      { option: 'tag-lin', label: 'Lin' },
      // Plagg-stiler — overdeler
      { option: 'tag-bluse', label: 'Bluse' },
      { option: 'tag-skjorte', label: 'Skjorte' },
      { option: 'tag-korsett', label: 'Korsett' },
      { option: 'tag-bandeau', label: 'Bandeau' },
      { option: 'tag-polo', label: 'Polo' },
      // Plagg-stiler — jakker
      { option: 'tag-bomber', label: 'Bomberjakke' },
      { option: 'tag-vind', label: 'Vindjakke' },
      { option: 'tag-faux-fur', label: 'Faux fur' },
      { option: 'tag-quiltet', label: 'Quiltet' },
      // Kjole-stiler
      { option: 'tag-maxikjole', label: 'Maxikjole' },
      { option: 'tag-midikjole', label: 'Midikjole' },
      { option: 'tag-minikjole', label: 'Minikjole' },
      { option: 'tag-slip', label: 'Slip dress' },
      { option: 'tag-skjortekjole', label: 'Skjortekjole' },
      { option: 'tag-omslag', label: 'Omslagskjole' },
      { option: 'tag-festkjole', label: 'Festkjole' },
      { option: 'tag-hverdagskjole', label: 'Hverdagskjole' },
      { option: 'tag-kaftan', label: 'Kaftan' },
      // Bukse-stiler
      { option: 'tag-wide-leg', label: 'Wide leg' },
      { option: 'tag-flare', label: 'Flare' },
      { option: 'tag-straight', label: 'Straight' },
      { option: 'tag-skinny', label: 'Skinny' },
      { option: 'tag-mom', label: 'Mom jeans' },
      { option: 'tag-boyfriend', label: 'Boyfriend' },
      { option: 'tag-palazzo', label: 'Palazzo' },
      { option: 'tag-jogge', label: 'Joggebukser' },
      { option: 'tag-leggings', label: 'Leggings' },
      { option: 'tag-capri', label: 'Capri' },
      // Skjørt-stiler
      { option: 'tag-maxiskjort', label: 'Maxiskjørt' },
      { option: 'tag-midiskjort', label: 'Midiskjørt' },
      { option: 'tag-miniskjort', label: 'Miniskjørt' },
      { option: 'tag-plissert', label: 'Plissert' },
      { option: 'tag-a-form', label: 'A-form' },
      { option: 'tag-blyant', label: 'Blyantskjørt' },
      // Sko-stiler
      { option: 'tag-hoye-haler', label: 'Høye hæler' },
      { option: 'tag-lave-haler', label: 'Lave hæler' },
      { option: 'tag-blokk', label: 'Blokkhæl' },
      { option: 'tag-kile', label: 'Kilehæl' },
      { option: 'tag-loafers', label: 'Loafers' },
      { option: 'tag-ballerina', label: 'Ballerinaer' },
      { option: 'tag-mokk', label: 'Mokkasiner' },
      { option: 'tag-slingback', label: 'Slingbacks' },
      { option: 'tag-ankel', label: 'Ankelboots' },
      { option: 'tag-kne', label: 'Knehøye støvler' },
      // Anledning (drives editorials)
      { option: 'tag-fest', label: 'Fest' },
      { option: 'tag-bryllup', label: 'Bryllup' },
      { option: 'tag-17mai', label: '17. mai' },
      { option: 'tag-konfirm', label: 'Konfirmasjon' },
      { option: 'tag-jobb', label: 'Jobb' },
      { option: 'tag-hverdag', label: 'Hverdag' },
      { option: 'tag-sommer', label: 'Sommerferie' },
      { option: 'tag-hytte', label: 'Hytteliv' },
      { option: 'tag-galla', label: 'Galla' },
      { option: 'tag-cocktail', label: 'Cocktail' },
      { option: 'tag-strand', label: 'Strand & ferie' },
    ],
    // Internal-only after the May 2026 client review — Plaggtype is the
    // AI-generated style/silhouette/anledning tag layer (drives editorial
    // collections + richer browse rails). No filterConfig (not a buyer-
    // facing filter), no showConfig (not surfaced on the listing page),
    // no saveConfig (not in the seller form). The data still rides on
    // publicData.plaggtype for AI consumers / editorial queries.
  },
  {
    key: 'premium',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'standard', label: 'Standard' },
      { option: 'premium', label: 'Premium' },
    ],
    // No filterConfig — premium is for internal sorting/promotion, not a
    // user-facing filter. (Validator rejects filterConfig without a label.)
    showConfig: { label: 'Premium', isDetail: false },
    // No saveConfig — auto-set by AI job from brand + price.
  },
  {
    key: 'sesong',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'var-sommer', label: 'Vår–sommer' },
      { option: 'host-vinter', label: 'Høst–vinter' },
      { option: 'helars', label: 'Helårs' },
    ],
    // Internal-only after the May 2026 client review — Sesong is an
    // AI-inferred tag. Not surfaced anywhere on the UI; data still rides
    // on publicData.sesong for downstream consumers.
  },

  {
    key: 'lengde',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'mini', label: 'Mini' },
      { option: 'midi', label: 'Midi' },
      { option: 'maxi', label: 'Maxi' },
    ],
    // Internal-only after the May 2026 client review — Lengde is no
    // longer in the seller form, on the listing page, or as a filter.
    // Definition kept so existing publicData.lengde values are still
    // valid; any future re-introduction just needs the save/show/filter
    // configs added back here.
  },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types                           //
///////////////////////////////////////////////////////////////////////

/**
 * Listing types are managed in Sharetribe Console as a hosted asset.
 * This local list is only consulted as a fallback when the hosted asset
 * is empty (fresh marketplace, tests, dev without Console connection).
 *
 * See the Sharetribe Web Template README for the original example
 * entries (daily-booking, nightly-booking, product-selling, inquiry, …)
 * if you ever need a starting template.
 */
export const listingTypes = [];

// Should listing type be enforced to a single value?
// Set to true when the marketplace has more than one listing type and
// extended-data search filters depend on listing type.
export const enforceValidListingType = false;
